import { superscriptSymbols } from "hyam-core/utils/superscriptSymbols";
import _ from "lodash";
import { MenuDocument } from "prismicio-types";
import { withConfigure } from "hyam-core/utils/withConfigure";
import {
  constants,
  styles as moduleStyles,
  HyamPrismicDocumentMenuMobileStyles,
} from "./styles";
import { PrismicNextImage } from "@prismicio/next";
import { dispatchEvent } from "hyam-core/utils/eventTarget";
import CloseIcon from "hyam-core/svg/hamburger-close-24px.svg";
import { HyamMenuItem } from "../HyamPrismicDocumentMenu/HyamPrismicDocumentMenu";
import {
  HyamPrismicButton,
  HyamPrismicLangSwitchRow,
} from "hyam-prismic/components/elements";
import { useState } from "react";
import ArrowRight from "svg/arrow-right-24px.svg";
import ArrowLeft from "svg/arrow-left-24px.svg";
import { classNamesWithMerge as cn } from "hyam-core/utils/tailwindMerge";

export type HyamPrismicDocumentMenuMobileProps = {
  document: MenuDocument;
  styles?: Partial<HyamPrismicDocumentMenuMobileStyles>;
  stylesReplace?: Partial<HyamPrismicDocumentMenuMobileStyles>;
};

type PanelProps = HyamPrismicDocumentMenuMobileProps & {
  items: HyamMenuItem[];
  backItem?: HyamMenuItem;
  onSubmenuClick?: (itemIndex: number) => void;
  onBackClick?: () => void;
  depth: number;
  activeDepth: number;
};

const Panel = (props: PanelProps) => {
  const styles = props.styles as HyamPrismicDocumentMenuMobileStyles;
  const {
    items,
    backItem,
    onSubmenuClick = () => {},
    onBackClick = () => {},
    depth,
    activeDepth,
  } = props;

  const styles_panel = cn(styles.panel, {
    [styles.panel_inactive]: activeDepth !== depth,
  });

  return (
    <div className={styles_panel}>
      {depth > 0 && (
        <div className={styles.panel_back_item}>
          <div className={styles.panel_back_arrow} onClick={onBackClick}>
            <ArrowLeft />
          </div>
          <div className={styles.panel_back_item_button_container}>
            <HyamPrismicButton
              styles={{ container_contained: styles.panel_back_item_button }}
              field={backItem?.link}
            >
              {superscriptSymbols(backItem?.title)}
            </HyamPrismicButton>
          </div>
        </div>
      )}
      {_.map(items, (item, index) => {
        const hasSubmenu = !!item?.submenu?.data;
        return (
          <HyamPrismicButton
            key={index}
            styles={{ container_contained: styles.panel_item }}
            onClick={hasSubmenu ? () => onSubmenuClick(index) : undefined}
            field={hasSubmenu ? undefined : item?.link}
          >
            <div className={styles.panel_item_title}>{superscriptSymbols(item?.title)}</div>
            {hasSubmenu && <ArrowRight className={styles.panel_item_arrow} />}
          </HyamPrismicButton>
        );
      })}
    </div>
  );
};

const _HyamPrismicDocumentMenuMobile = (props: HyamPrismicDocumentMenuMobileProps) => {
  const styles = props.styles as HyamPrismicDocumentMenuMobileStyles;
  const { document } = props;
  const { data } = document;

  // Example:
  // []     -> /
  // [0]    -> /treatments
  // [1]    -> /experience
  // [0, 0] -> /treatments/visalign
  const [panelStack, setPanelStack] = useState([] as number[]);

  const doClose = () => {
    dispatchEvent({ name: "hide_nav_bar_drawer" });
    setTimeout(() => {
      setPanelStack([]);
    }, 500);
  };

  const items0 = data?.items as HyamMenuItem[];

  const menu1 = data?.items?.[panelStack?.[0]]?.submenu as any as MenuDocument;
  const items1 = menu1?.data?.items as HyamMenuItem[];

  const menu2 = items1?.[panelStack?.[1]]?.submenu as any as MenuDocument;
  const items2 = menu2?.data?.items as HyamMenuItem[];

  const activeDepth = (() => {
    if (!_.isEmpty(items2)) return 2;
    if (!_.isEmpty(items1)) return 1;
    return 0;
  })();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <PrismicNextImage
          height={constants.logoHeight}
          className={styles.logo}
          field={data.logo}
        />
        <p className={styles.logo_subheadline}>{data?.logo_subheadline}</p>
      </div>
      <Panel
        {...props}
        items={items0}
        depth={0}
        activeDepth={activeDepth}
        onSubmenuClick={(itemIndex) => {
          setPanelStack([itemIndex]);
        }}
      />
      <Panel
        {...props}
        items={items1}
        backItem={items0?.[panelStack[0]]}
        depth={1}
        activeDepth={activeDepth}
        onSubmenuClick={(itemIndex) => {
          setPanelStack([panelStack[0], itemIndex]);
        }}
        onBackClick={() => {
          setPanelStack([]);
        }}
      />
      <Panel
        depth={2}
        activeDepth={activeDepth}
        {...props}
        items={items2}
        backItem={items1?.[panelStack[1]]}
        onBackClick={() => {
          setPanelStack([panelStack[0]]);
        }}
      />
      <HyamPrismicLangSwitchRow styles={{ container: styles.lang_switch }} />
      <div className={styles.close_button}>
        <CloseIcon onClick={doClose} />
      </div>
    </div>
  );
};

export const HyamPrismicDocumentMenuMobile = withConfigure<
  HyamPrismicDocumentMenuMobileProps,
  HyamPrismicDocumentMenuMobileStyles
>({
  componentName: "HyamPrismicDocumentMenuMobile",
  Component: _HyamPrismicDocumentMenuMobile,
  moduleStyles,
});
